import { motion } from 'framer-motion';
import { ShieldOff, Mail, Clock, LogOut } from 'lucide-react';
import { Button } from './ui/button';

interface LicenseExpiredScreenProps {
  clientName: string;
  clientEmail: string;
  expiredAt?: string;
  onLogout: () => void;
}

export function LicenseExpiredScreen({ clientName, clientEmail, expiredAt, onLogout }: LicenseExpiredScreenProps) {
  const formattedDate = expiredAt
    ? new Date(expiredAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Unknown date';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-6">
      {/* Background animated orbs */}
      <motion.div
        className="absolute top-[-150px] left-[-150px] w-[500px] h-[500px] rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #ef4444, transparent)' }}
        animate={{ scale: [1, 1.15, 1], rotate: [0, 20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-120px] right-[-120px] w-[400px] h-[400px] rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #f97316, transparent)' }}
        animate={{ scale: [1.1, 1, 1.1], rotate: [0, -15, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-10 max-w-lg w-full bg-card/70 backdrop-blur-xl border border-red-500/20 rounded-3xl p-10 shadow-2xl shadow-red-500/10"
      >
        {/* Icon */}
        <motion.div
          initial={{ rotate: -10 }}
          animate={{ rotate: [0, -6, 6, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="mx-auto w-24 h-24 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mb-8"
        >
          <ShieldOff className="h-12 w-12 text-red-500" />
        </motion.div>

        {/* Headline */}
        <h1 className="text-3xl font-bold text-center text-foreground mb-2">
          License Expired
        </h1>
        <p className="text-center text-muted-foreground mb-8 leading-relaxed">
          Your Factreon dashboard license for <span className="font-semibold text-foreground">{clientName}</span> has expired. 
          Access to all features is currently suspended.
        </p>

        {/* Info cards */}
        <div className="space-y-3 mb-8">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/15">
            <div className="p-2 rounded-lg bg-red-500/10">
              <Clock className="h-4 w-4 text-red-500" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Expired On</p>
              <p className="text-sm font-semibold text-foreground">{formattedDate}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-500/5 border border-blue-500/15">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Mail className="h-4 w-4 text-blue-500" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Notification Sent To</p>
              <p className="text-sm font-semibold text-foreground">{clientEmail}</p>
            </div>
          </div>
        </div>

        {/* Instruction */}
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-8 text-center">
          <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
            Please contact your <strong>Super Administrator</strong> to renew your license and restore access.
          </p>
        </div>

        {/* Logout */}
        <Button
          onClick={onLogout}
          variant="outline"
          className="w-full h-11 gap-2 border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>

        {/* Footer */}
        <p className="text-center text-[11px] text-muted-foreground/40 mt-6 tracking-widest uppercase">
          Factreon · License Management
        </p>
      </motion.div>
    </div>
  );
}
